import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { TourDocument } from "@heroncendre/schema";
import {
  pickText,
  useGuidedOptional,
  usePanoramaProgress,
  useTourState,
  useTourStoreApi,
} from "@heroncendre/player";
import { Logo } from "./Bandeau.js";
import { Credits } from "./Credits.js";
import { SelecteurLangue } from "./SelecteurLangue.js";
import { accueil, estCredits, estParcours, type Item } from "./choix.js";
import { parcoursExiste } from "./parcours.js";
import { useReducedMotion } from "./useReducedMotion.js";
import * as s from "./splashStyles.js";
import "./splash.css";

/**
 * ============================================================================
 *  SplashScreen.tsx — L'écran d'accueil « Écrin »
 * ----------------------------------------------------------------------------
 *  Posé sur le panorama d'entrée, avant la visite : il propose la visite libre,
 *  un parcours commenté, ou les crédits. Référence de design :
 *  `sources/design/design_handoff_splash_ecrin/`.
 *
 *  ── Le fond est la vraie scène ─────────────────────────────────────────────
 *
 *  Rien n'est dessiné ici : l'écran est un dégradé sombre et un flou
 *  (`backdrop-filter`) posés sur le canvas du moteur, qui continue d'afficher la
 *  scène d'entrée. Son orientation reste celle d'entrée ; seul un zoom / dézoom
 *  lent l'anime, appliqué en CSS au canvas par CET écran —
 *  échelle et période propres à l'accueil (`accueil.json`), sans toucher au
 *  réglage du document, qui vaut pour toute la visite. À la sortie, le flou s'efface et le panorama
 *  redevient net : c'est le fondu vers la visite.
 *
 *  ── Pourquoi le clic « amorce » l'audio ────────────────────────────────────
 *
 *  Les navigateurs refusent un son que l'utilisateur n'a pas demandé, iOS en
 *  tête : le clic sur un item EST ce geste. `controller.prime()` est appelé
 *  dans le gestionnaire, de façon synchrone, avant tout changement d'état — voir
 *  aussi `ParcoursGuide`. Le lecteur de voix off existe dès l'accueil parce que
 *  l'application donne toujours un parcours au player à ce stade.
 *
 *  ── La progression est celle du moteur ─────────────────────────────────────
 *
 *  `usePanoramaProgress()` : les tuiles du niveau de base reçues, sur celles
 *  attendues. Le préchargement ne bloque JAMAIS le menu. Si le visiteur choisit
 *  avant la fin, l'écran reste en état de sortie et n'ouvre la visite que
 *  lorsque la scène est prête.
 *
 *  Les textes sont dans `accueil.json`, jamais ici.
 * ==========================================================================*/

export interface SplashScreenProps {
  tour: TourDocument;
  assetBaseUrl: string;
  /** Appelé une fois l'écran effacé ET la scène prête. Jamais pour les crédits. */
  onSelect(item: Item): void;
}

/** Instants d'apparition (ms depuis le montage) : fond, logo, titre, menu. */
const ENTREE_MS = [150, 1300, 2000, 2600];
const ENTREE_REDUITE_MS = [50, 450, 750, 1050];

/** Du clic à la sortie : le temps de voir l'item se sélectionner. */
const AVANT_SORTIE_MS = 450;
/** De la sortie à l'ouverture de la visite : le temps que le flou s'efface. */
const SORTIE_MS = 2000;
const SORTIE_REDUITE_MS = 400;
/** Sortie vers un parcours guidé : le temps de passer au noir (.7 s), plus un souffle. Le guide s’y enchaîne. */
const SORTIE_NOIR_MS = 900;

const disponible = (item: Item): boolean =>
  estCredits(item) || !estParcours(item) || parcoursExiste(item.parcours!);

export function SplashScreen({ tour, assetBaseUrl, onSelect }: SplashScreenProps) {
  const store = useTourStoreApi();
  const language = useTourState((state) => state.context.language);
  const guide = useGuidedOptional();
  const progression = usePanoramaProgress();
  const reduit = useReducedMotion();

  // `phase` : 0 rien, 1 fond, 2 logo, 3 titre, 4 menu et barres.
  const [phase, setPhase] = useState(0);
  const [actif, setActif] = useState(-1);
  const [choisi, setChoisi] = useState(-1);
  const [sortie, setSortie] = useState(false);
  const [credits, setCredits] = useState(false);
  const sortieDepuis = useRef(0);
  const menuRef = useRef<HTMLElement>(null);
  const ouvert = useRef(false);

  const t = (texte: Record<string, string>) => pickText(texte, language) ?? "";
  const items = accueil.items;

  /* --- entrée en scène ----------------------------------------------------- */

  useEffect(() => {
    const instants = reduit ? ENTREE_REDUITE_MS : ENTREE_MS;
    const minuteurs = instants.map((ms, index) =>
      window.setTimeout(() => {
        setPhase(index + 1);
        // Comme un menu de jeu : le premier item est actif d'emblée.
        if (index === 3) setActif((courant) => (courant < 0 ? 0 : courant));
      }, ms),
    );
    return () => minuteurs.forEach(window.clearTimeout);
  }, [reduit]);

  /* --- le fond : rotation lente de la vraie scène -------------------------- */

  useEffect(() => {
    // La rotation au repos du moteur est suspendue le temps de l'accueil : deux
    // sources d'écriture du cap se doubleraient. Sans préférence (`undefined`),
    // le réglage du document reprend ensuite la main.
    store.getState().setAutoRotatePreference(false);
    if (reduit) return () => store.getState().setAutoRotatePreference(undefined);

    // Le cap ne bouge pas : l'orientation d'entrée est conservée pendant que le
    // visiteur choisit. Seul un zoom / dézoom lent l'anime — en CSS sur le canvas
    // du moteur, PAS dans le store : écrire le fov à chaque image fait changer de
    // niveau de tuiles au moteur et la scène disparaît (page figée).
    const { zoomEchelle, zoomPeriodeSecondes } = accueil;
    const canvas = document.querySelector("canvas");
    if (!canvas) return () => store.getState().setAutoRotatePreference(undefined);
    const avant = canvas.style.transform;
    const origine = canvas.style.transformOrigin;
    canvas.style.transformOrigin = "50% 50%";
    let image = 0;
    const debut = performance.now();
    const pas = (maintenant: number) => {
      // Part de 1 (pas de saut), monte à `zoomEchelle`, revient.
      const phase = ((maintenant - debut) / 1000 / zoomPeriodeSecondes) * 2 * Math.PI;
      const echelle = 1 + (zoomEchelle - 1) * 0.5 * (1 - Math.cos(phase));
      canvas.style.transform = `scale(${echelle.toFixed(5)})`;
      image = requestAnimationFrame(pas);
    };
    image = requestAnimationFrame(pas);
    return () => {
      cancelAnimationFrame(image);
      canvas.style.transform = avant;
      canvas.style.transformOrigin = origine;
      store.getState().setAutoRotatePreference(undefined);
    };
  }, [store, reduit]);

  /* --- sélection et sortie ------------------------------------------------- */

  const choisir = (index: number) => {
    const item = items[index];
    if (!item || sortie || !disponible(item)) return;
    if (estCredits(item)) {
      setActif(index);
      setCredits(true);
      return;
    }
    // Dans le gestionnaire du clic, synchrone, avant tout changement d'état.
    guide?.controller.prime();
    setChoisi(index);
    setActif(index);
    window.setTimeout(() => {
      sortieDepuis.current = performance.now();
      setSortie(true);
    }, AVANT_SORTIE_MS);
  };

  // `pending` : la scène n'est pas affichable. `missing` compte comme prête — une
  // scène sans panorama n'en aura jamais, le décor de grille est déjà là.
  const prete = progression.status !== "pending";

  // Un parcours guidé : la sortie passe par le NOIR (le guide s’y enchaîne, voir `FonduEntree`) ; la visite
  // libre, elle, révèle la scène derrière l’accueil.
  const itemChoisi = items[choisi];
  const vaAuNoir = itemChoisi !== undefined && estParcours(itemChoisi);

  const surSelection = useRef(onSelect);
  surSelection.current = onSelect;

  useEffect(() => {
    if (!sortie || !prete || ouvert.current) return;
    const duree = reduit ? SORTIE_REDUITE_MS : vaAuNoir ? SORTIE_NOIR_MS : SORTIE_MS;
    const reste = Math.max(0, duree - (performance.now() - sortieDepuis.current));
    const minuteur = window.setTimeout(() => {
      const item = items[choisi];
      if (!item) return;
      ouvert.current = true;
      surSelection.current(item);
    }, reste);
    return () => window.clearTimeout(minuteur);
  }, [sortie, prete, reduit, choisi, items, vaAuNoir]);

  /* --- clavier ------------------------------------------------------------- */

  const surTouche = (event: KeyboardEvent<HTMLElement>) => {
    const touches = ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"];
    if (!touches.includes(event.key)) return;
    const boutons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("button[data-item]:not(:disabled)")];
    if (boutons.length === 0) return;
    const n = boutons.length;
    const courant = boutons.indexOf(document.activeElement as HTMLButtonElement);
    const suivant =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? n - 1
          : event.key === "ArrowDown" || event.key === "ArrowRight"
            ? (courant + 1) % n
            : (courant - 1 + n) % n;
    event.preventDefault();
    boutons[suivant]?.focus();
  };

  /* --- chargement ---------------------------------------------------------- */

  /* --- clavier global : flèches + Entrée sans avoir cliqué ni tabulé --------- */

  const clavier = useRef<(event: globalThis.KeyboardEvent) => void>(() => {});
  clavier.current = (event) => {
    // La touche a déjà été prise (flèches DANS le menu, Échap des crédits).
    if (event.defaultPrevented) return;

    if (credits) {
      if (event.key === "Escape") setCredits(false);
      return;
    }
    if (sortie || phase < 4) return;

    const cible = event.target;
    const surBouton = cible instanceof HTMLButtonElement;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const boutons = [
        ...(menuRef.current?.querySelectorAll<HTMLButtonElement>("button[data-item]:not(:disabled)") ?? []),
      ];
      if (boutons.length === 0) return;
      const n = boutons.length;
      // Depuis l'item actif (celui que le survol ou le dernier choix a posé),
      // et non depuis le focus : au clavier sans focus, c'est lui qu'on voit.
      const depart = boutons.findIndex((b) => b.dataset.item === String(actif));
      const suivant =
        depart < 0
          ? event.key === "ArrowDown"
            ? 0
            : n - 1
          : (depart + (event.key === "ArrowDown" ? 1 : -1) + n) % n;
      event.preventDefault();
      boutons[suivant]?.focus();
      return;
    }

    // Entrée sur un bouton : le navigateur le clique déjà. Sans focus, on valide
    // l'item actif — c'est ce que le visiteur voit en surbrillance.
    if (event.key === "Enter" && !surBouton && actif >= 0) {
      event.preventDefault();
      choisir(actif);
    }
  };

  useEffect(() => {
    const surTouche = (event: globalThis.KeyboardEvent) => clavier.current(event);
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, []);

  const pourcent = prete ? 100 : Math.round(progression.fraction * 100);
  const libelleCharge = prete ? t(accueil.pret) : `${t(accueil.chargement)} · ${pourcent} %`;

  const affiche = phase >= 4;

  return (
    <section aria-label={t(accueil.bienvenue)} style={s.racine}>
      <div aria-hidden style={s.teinte(!sortie || vaAuNoir)} />
      <div aria-hidden style={s.voile(phase >= 1 && !(sortie && vaAuNoir), vaAuNoir, reduit)} />

      <div style={s.couche(sortie)}>
        <div style={s.barre(affiche, ".6s")}>
          <div style={s.son}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
              <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
              <rect x="3" y="14" width="4" height="6" rx="1.5" />
              <rect x="17" y="14" width="4" height="6" rx="1.5" />
            </svg>
            <span>{t(accueil.son)}</span>
          </div>

          <SelecteurLangue />
        </div>

        <main style={s.principal}>
          <Logo tour={tour} assetBaseUrl={assetBaseUrl} style={s.logo(phase >= 2, reduit)} />
          <h1 style={s.titre(phase >= 3, reduit)}>{t(accueil.bienvenue)}</h1>
          <div aria-hidden style={s.filet(phase >= 3)}>
            <span style={s.traitFilet} />
            <span style={s.losangleFilet} />
            <span style={s.traitFilet} />
          </div>

          <nav ref={menuRef} aria-label="Menu" onKeyDown={surTouche} style={s.menu}>
            {items.map((item, index) => {
              const libre = disponible(item);
              const etat: s.EtatItem = !libre
                ? "desactive"
                : choisi === index
                  ? "choisi"
                  : actif === index
                    ? "actif"
                    : "repos";
              const sousTitre = item.sousTitre ? t(item.sousTitre) : "";
              const idDescription = `splash-sous-titre-${item.id}`;
              return (
                <div key={item.id} style={s.ligneItem(affiche, reduit, index)}>
                  <button
                    type="button"
                    data-item={index}
                    disabled={!libre}
                    tabIndex={affiche ? 0 : -1}
                    title={libre ? undefined : t(accueil.bientot)}
                    aria-describedby={sousTitre ? idDescription : undefined}
                    onClick={() => choisir(index)}
                    // Survol et focus clavier sont UN SEUL état actif, qui reste
                    // après `mouseleave`.
                    onMouseEnter={() => !sortie && libre && setActif(index)}
                    onFocus={() => !sortie && libre && setActif(index)}
                    style={s.boutonItem(etat)}
                  >
                    <span aria-hidden style={s.marqueItem(etat)} />
                    <span style={s.libelleItem}>{t(item.titre)}</span>
                    <span aria-hidden style={s.marqueItem(etat)} />
                  </button>
                  {sousTitre && (
                    <>
                      <span id={idDescription} className="splash-sr">
                        {sousTitre}
                      </span>
                      <div aria-hidden style={s.sousTitre(etat === "actif" || etat === "choisi")}>
                        <div style={{ padding: "7px 0 6px" }}>{sousTitre}</div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </nav>
        </main>

        <div style={{ ...s.pied, opacity: affiche ? 1 : 0, transition: "opacity 1s ease .9s" }}>
          {/* Le libellé visuel change à chaque pour cent ; le lecteur d'écran, lui,
              n'en entend que deux états — en cours, puis prête. */}
          <div aria-hidden style={s.etatChargement}>
            <span style={s.pointChargement(!reduit && !prete)} />
            <span>{libelleCharge}</span>
          </div>
          <span className="splash-sr" aria-live="polite">
            {prete ? t(accueil.pret) : t(accueil.chargement)}
          </span>
          <div style={s.signature}>{t(accueil.signature)}</div>
        </div>

        <div aria-hidden style={s.pisteProgression(affiche && !prete)}>
          <div style={s.remplissage(pourcent)} />
        </div>
      </div>

      <div aria-live="polite" style={s.pastilleEntree(sortie)}>
        <div style={s.pastilleContenu}>
          <span style={s.pointChargement(!reduit)} />
          {t(accueil.entree)}
        </div>
      </div>

      {credits && (
        <Credits assetBaseUrl={assetBaseUrl} onClose={() => setCredits(false)} />
      )}
    </section>
  );
}
